import { ReactNode, useState } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import * as Yup from 'yup';

import { InputPassword } from '@/@core/SuperInputPassword';
import { useAuthenticate } from '@/context/AuthenticateContext';
import { Button, ErrorMessage } from '@/@core';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { CONFIG_MAIN_TOAST } from '@/constants';

interface IForm {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

const initialValues: IForm = {
  current_password: '',
  new_password: '',
  new_password_confirmation: '',
};

interface Props {
  onSuccess(): void;
  onCancel(): void;
}

export const validationSchema = Yup.object().shape({
  current_password: Yup.string().required('Field Required'),
  new_password: Yup.string().trim().min(8).required('Field Required'),
  new_password_confirmation: Yup.string()
    .trim()
    .min(8)
    .required('Field Required')
    .oneOf([Yup.ref('new_password')], 'Passwords does not match'),
});

const ChangePasswordForm = ({ onSuccess, onCancel }: Props): ReactNode => {
  const { changePassword } = useAuthenticate();
  const [error, setError] = useState<string | undefined>();

  const onSubmit = async (formData: IForm, actions: FormikHelpers<IForm>) => {
    try {
      actions.setSubmitting(true);
      const response = await changePassword(formData);
      const { message } = response.data;
      toast.success(message || 'Password updated!', CONFIG_MAIN_TOAST);
      onSuccess();
    } catch (error) {
      if (error instanceof AxiosError) {
        const err = error.response?.data?.error;
        if (err) {
          setError(err);
        }
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  const { handleSubmit, handleChange, values, errors, isSubmitting } = useFormik<IForm>({
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
    initialValues,
    onSubmit,
  });

  return (
    <form className="grid grid-cols-12 gap-2" onSubmit={handleSubmit} autoComplete="off">
      <div className="col-span-12">
        <ErrorMessage error={error} />
      </div>
      <div className="col-span-12">
        <InputPassword
          id="current-password"
          label={'Current Password'}
          onChange={handleChange}
          name="current_password"
          value={values.current_password}
          error={errors.current_password}
        />
      </div>
      <div className="col-span-12 sm:col-span-6">
        <InputPassword
          id="new-password"
          label={'New Password'}
          onChange={handleChange}
          name="new_password"
          value={values.new_password}
          error={errors.new_password}
        />
      </div>
      <div className="col-span-12 sm:col-span-6">
        <InputPassword
          id="confirm-new-password"
          label={'Confirm New Password'}
          onChange={handleChange}
          name="new_password_confirmation"
          value={values.new_password_confirmation}
          error={errors.new_password_confirmation}
        />
      </div>
      <div className="col-span-12 mt-6 space-x-1 text-end">
        <Button color="light" className="!text-xs" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" color="success" className="!text-xs" disabled={isSubmitting}>
          Change
        </Button>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
